/**
 * @file ShopCreateMainFields.tsx
 * @description Поля «Название», «Адрес (slug)», «Часовой пояс», «Почта
 * магазина» формы «Новый магазин» (phase19.28 этап А; uiux-platform-shops
 * §3; PL4, PL5). Контракт `value / onChange` — общий с `ShopOwnerFields`.
 *
 * Slug заполняется транслитом из названия, пока его не правили руками;
 * под полем — проверка по общему правилу и адрес витрины
 * `https://<slug>.commora.ru`. Занятость проверяет сервер при создании.
 */

import { buildSubdomainHost, validateShopSlug } from "@chaiberi/shared";
import { CircleCheck } from "lucide-react";

import type { ShopSlugCheck } from "../../../lib/api-platform-shops";
import type { ShopTimeZoneOption } from "../../../lib/api-shop-settings";
import { usePlatformEdgeConfig } from "../../../hooks/platform/usePlatformEdgeConfig";
import { slugIssue, withDisplayName, type ShopCreateErrors, type ShopCreateValue } from "../../../lib/platform/shop-create-form";
import { ShopTimeZoneField } from "../../../pages/shop/settings/ShopTimeZoneField";
import { ShopTextField } from "./ShopTextField";

interface Props {
  value: ShopCreateValue;
  onChange: (next: ShopCreateValue) => void;
  errors: ShopCreateErrors;
  timeZones: ShopTimeZoneOption[];
  /** Ответ сервера «свободен ли адрес»; null — ещё не проверили. */
  slugCheck: ShopSlugCheck | null;
  disabled?: boolean;
}

/** Подсказка под slug: правило, затем ответ сервера «свободен» и адрес витрины. */
function SlugHint({ slug, check }: { slug: string; check: ShopSlugCheck | null }) {
  const { baseDomain } = usePlatformEdgeConfig();
  if (!slug) return <>Латиница, цифры и дефис. Адрес не меняется после создания</>;
  const issue = slugIssue(slug);
  if (issue || !validateShopSlug(slug).data) return <>{issue}</>;
  const host = check?.subdomainHost ?? buildSubdomainHost(slug, baseDomain);
  return (
    <span className="inline-flex items-start gap-1">
      <CircleCheck aria-hidden className="mt-0.5 size-3.5 shrink-0 text-[var(--status-success-fg,var(--success))]" />
      <span className="break-all">{check?.available ? "Свободен" : "Подходит"}. Витрина: https://{host}</span>
    </span>
  );
}

/** Сервер ответил «занят / зарезервирован» — это ошибка поля. */
function slugCheckError(check: ShopSlugCheck | null): string | undefined {
  return check && !check.available ? (check.message ?? "Этот адрес занять нельзя — выберите другой") : undefined;
}

export function ShopCreateMainFields({ value, onChange, errors, timeZones, slugCheck, disabled }: Props) {
  const slugError = errors.slug ?? (value.slug ? (slugIssue(value.slug) ?? slugCheckError(slugCheck)) : undefined);
  return (
    <div className="flex flex-col gap-4">
      <ShopTextField
        id="shop-name"
        label="Название"
        required
        value={value.displayName}
        onChange={(displayName) => onChange(withDisplayName(value, displayName))}
        error={errors.displayName}
        maxLength={120}
        autoComplete="off"
        disabled={disabled}
      />
      <ShopTextField
        id="shop-slug"
        label="Адрес (slug)"
        required
        value={value.slug}
        onChange={(slug) => onChange({ ...value, slug: slug.trim().toLowerCase(), slugTouched: true })}
        error={slugError}
        hint={<SlugHint slug={value.slug} check={slugCheck} />}
        maxLength={40}
        autoComplete="off"
        spellCheck={false}
        inputMode="url"
        disabled={disabled}
      />
      <ShopTimeZoneField
        value={value.timeZone}
        options={timeZones}
        onChange={(timeZone) => onChange({ ...value, timeZone })}
        error={errors.timeZone}
        disabled={disabled}
        required
      />
      <ShopTextField
        id="shop-contact-email"
        label="Почта магазина"
        type="email"
        value={value.contactEmail}
        onChange={(contactEmail) => onChange({ ...value, contactEmail })}
        error={errors.contactEmail}
        hint="Необязательно. Владелец заполнит её в своём кабинете"
        autoComplete="off"
        disabled={disabled}
      />
    </div>
  );
}
